import { Tooltip } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import type { TOC, TOCChild, TOCParent } from '~/common/types';
import Icon from '~/components2/Icon';
import { PickerCheckbox } from '~/components2/Map/Widgets/Layers/PickerCheckbox';
import { DOWNLOAD_LABELS, type ExportLabels } from '~/hooks/useExportTerminologyEnabled';
import layerStore from '~/store2/layers';
import { toMapPreviewLayerId } from '../../downloads/extract/siteDataLayers';
import { expansionKey, type PickerGroup } from '../pickerTree';
import store from '../store';

export type LayerPickerProps = {
  currentObjectArea: number | null;
  /** Extra sections rendered above the standard tree (e.g. the extract tab's Custom Data group),
   * matching the map TOC which pins Custom Data to the top.
   * Render-time only — never merged into the store, so async refreshes can't race or wipe them. */
  extraSections?: TOCParent[];
  /**
   * Copy for the restriction and unknown-assets warnings. Defaults to download wording because the
   * legacy ESRI form renders this too and is excluded from the rename; the FME form passes flag-gated labels.
   */
  labels?: ExportLabels;
};

type RenderOptions = {
  group: PickerGroup;
  showCheckbox: boolean;
};

type PickerRowsArgs = LayerPickerProps & {
  searchTerm: string;
  searchExpansion?: ReadonlyMap<string, boolean>;
  onSearchExpansionChange?: (key: string, expanded: boolean) => void;
};

export function usePickerRows({
  currentObjectArea,
  extraSections,
  labels = DOWNLOAD_LABELS,
  searchTerm,
  searchExpansion,
  onSearchExpansionChange,
}: PickerRowsArgs) {
  const {
    selectedLayerIds,
    TOCLayers,
    outsideAreaTOCLayers,
    expandedSections,
    restrictions,
    layerIdsWithUnknownAssets,
    isCheckingLayerAssets,
    layerAssetPresenceFailed,
  } = useSnapshot(store);

  const handleLayerChange = (isVisible: boolean | 'indeterminate', layerId: string) => {
    if (isVisible === 'indeterminate') return;

    // add / remove layers from the download layer store AND the primary layer store so they are visible
    const mapLayerId = toMapPreviewLayerId(layerId);
    if (isVisible) {
      store.selectedLayerIds.add(layerId);
      layerStore.visibleLayerIds.add(mapLayerId);
    } else {
      store.selectedLayerIds.delete(layerId);
      layerStore.visibleLayerIds.delete(mapLayerId);
    }
  };

  useEffect(() => {
    // expand all sections on mount since layers are selected by default
    store.toggleAllSections(true);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: expand once per section arrival
  useEffect(() => {
    for (const section of extraSections ?? []) {
      store.expandedSections.add(expansionKey('extra', section));
    }
  }, [extraSections?.length]);

  const renderParent = (item: TOCParent, options: RenderOptions) => {
    const children = item.children
      .map((child) => {
        if (store.isTOCParent(child)) {
          return renderParent(child, options);
        } else {
          return renderChild(child, options);
        }
      })
      .filter((item) => !isEmpty(item));

    // none / some / all of the descendant leaves, matching the main map TOC's section rows
    const sectionCheckedState = store.getSectionCheckedState(item.children);
    const sectionKey = expansionKey(options.group, item);
    // A search reads its own expansion rather than expandedSections, so nothing toggled mid-search outlives it
    const isSearching = searchTerm !== '';
    const isExpanded = isSearching ? (searchExpansion?.get(sectionKey) ?? true) : expandedSections.has(sectionKey);

    // section is digitized if it has a child with an id starting with 'digitized'
    // this won't break unless we change our naming convention for digitized layers

    const isDigitizedSection = (item: TOCParent): boolean => {
      return item.children.some((child) => {
        if ('id' in child) {
          return child.id.startsWith('digitized');
        }

        if ('children' in child) {
          return isDigitizedSection(child as TOCParent);
        }
        return false;
      });
    };

    const returnSectionWarning = (item: TOCParent) => {
      const restriction = restrictions[item.label];

      // don't check to return a warning if the section is digitized
      if (isDigitizedSection(item)) {
        return null;
      }

      if (currentObjectArea && currentObjectArea > Number(restriction)) {
        return (
          <div className="text-2xs text-[#B28B00]">
            {restriction === 0
              ? labels.layerRequiresApproval
              : labels.layerRestrictedToAcres(Number(restrictions[item.label]))}
          </div>
        );
      }

      return null;
    };

    if (children.length === 0) {
      return null;
    }

    return (
      <div
        id="download-list"
        key={sectionKey}
        data-testid={`download-layer-section-${sectionKey}`}
        className={'my-2 px-1'}
      >
        <div className="group flex cursor-pointer items-center justify-between space-x-2 min-h-6">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              aria-label={`${item.label} section`}
              aria-expanded={isExpanded}
              data-testid={`download-layer-section-expand-${sectionKey}`}
              className="flex appearance-none border-0 bg-transparent p-0 rounded focus-ring"
              onClick={() => {
                if (isSearching) {
                  onSearchExpansionChange?.(sectionKey, !isExpanded);
                } else if (isExpanded) {
                  store.expandedSections.delete(sectionKey);
                } else {
                  store.expandedSections.add(sectionKey);
                }
              }}
            >
              <Icon icon={isExpanded ? 'chevronDown' : 'chevronRight'} />
            </button>

            <div className="flex flex-row items-center space-x-4">
              {options.showCheckbox ? (
                <PickerCheckbox
                  data-testid={`download-layer-section-toggle-${item.sectionId ?? item.label}`}
                  checked={sectionCheckedState}
                  onCheckedChange={(value) => {
                    // radix resolves indeterminate -> true on click, so this is defensive only
                    if (value === 'indeterminate') return;

                    store.toggleAllSectionLayers(item.children, value);
                  }}
                  label={item.label}
                />
              ) : (
                <div className="text-xs text-grey-60">{item.label}</div>
              )}
              {options.showCheckbox && returnSectionWarning(item)}
            </div>
          </div>
        </div>

        {isExpanded && <div className="my-2 ml-2.5">{children}</div>}
      </div>
    );
  };

  const renderChild = (item: TOCChild, options: RenderOptions) => {
    const isVisible = selectedLayerIds.has(item.id);

    return (
      <div
        key={item.id}
        data-testid={`download-layer-${options.group}-${item.id}`}
        className={
          // fixed height ensures no "jumping" when bookmark icon displays, makes the
          // line height just a bit bigger
          `group relative my-1 ml-7 flex h-6 flex-row items-center justify-between space-x-2`
        }
      >
        <div className="flex grow flex-row items-center space-x-2">
          {options.showCheckbox ? (
            <PickerCheckbox
              data-testid={`download-layer-toggle-${item.id}`}
              checked={isVisible}
              onCheckedChange={(isVisible) => {
                handleLayerChange(isVisible, item.id);
              }}
              label={item.label}
            />
          ) : (
            <div className="text-xs text-grey-60">{item.label}</div>
          )}
          {layerIdsWithUnknownAssets.has(item.id) && (
            <Tooltip
              text={
                <>
                  We couldn't check whether this layer has data in your project area.
                  <br />
                  {labels.layerAssetsUnknown}
                </>
              }
              delayDuration={200}
              sideOffset={10}
              className="max-w-[30rem]"
              aria-label={`Data check unavailable for ${item.label}`}
              triggerClassName="text-yellow-60"
              data-testid={`download-layer-unknown-assets-warning-${item.id}`}
            >
              <Icon icon="warning" size={12} />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const renderTree = (tree: TOC, options: RenderOptions) =>
    tree
      .map((item) => (store.isTOCParent(item) ? renderParent(item, options) : renderChild(item, options)))
      .filter((item) => !isEmpty(item));

  return {
    renderTree,
    selectedLayerIds,
    TOCLayers,
    outsideAreaTOCLayers,
    expandedSections,
    restrictions,
    layerIdsWithUnknownAssets,
    isCheckingLayerAssets,
    layerAssetPresenceFailed,
  };
}
